import { isValidCreatureAvatar, MAX_AVATAR_DATA_URL_LENGTH } from '@common/FabulaUltimaAvatar';

export class AvatarImageError extends Error {
  constructor(readonly reason: 'type' | 'size' | 'decode') { super(reason); }
}

export async function prepareCreatureAvatar(file: File): Promise<string> {
  if (!['image/jpeg', 'image/png'].includes(file.type)) throw new AvatarImageError('type');
  if (file.size > 10 * 1024 * 1024) throw new AvatarImageError('size');
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new AvatarImageError('decode'));
      element.src = url;
    });
    if (!image.naturalWidth || !image.naturalHeight) throw new AvatarImageError('decode');
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new AvatarImageError('decode');
    // Retain the entire image and PNG transparency. Only the displayed thumbnail is cropped.
    for (const edge of [256, 192, 128, 96, 64]) {
      const scale = Math.min(1, edge / Math.max(image.naturalWidth, image.naturalHeight));
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const result = canvas.toDataURL(file.type, 0.85);
      if (result.length <= MAX_AVATAR_DATA_URL_LENGTH && isValidCreatureAvatar(result)) return result;
    }
    throw new AvatarImageError('size');
  } finally {
    URL.revokeObjectURL(url);
  }
}
